import React from "react";
import { Alert, Form } from 'react-bootstrap'
import FullQuoteActions, { IBindQuoteResponse } from "../../state/fullQuoteActions";
import { IQuoteStepState } from "../../components/interfaces";
import { IAppState } from "../../state/interfaces";
import { connect } from "react-redux";
import { FULL_QUOTE_STEPS, COMPONENTS } from "../../AppConstants";
import { ValidatableForm } from "../../components/ValidatableForm"
import { IQuoteStepComponentProps } from "../../components/interfaces";
import { validationUtils } from "../../validation/Validation";
import { QuoteStep } from "./QuoteStepLayout";
import ButtonContinue from "../../components/ButtonContinue";
import QuoteStepBase from '../../components/QuoteStepBase'
import { IAddress, IAddressChangeResult, emptyAddress, IPaymentChoice, ICreditCardInfo, IPerson, IQuoteApiClient, ILOBApiClient, IPaths, IPaymentBag, IPaymentInfo, IPaymentInfoChangeResult, IEftInfo } from "../../classes/Domain";
import Address from "../../components/Address";
import ComplexCheckbox from "../../components/ComplexCheckbox";
import CreditCard from "../../components/CreditCard";
import Eft from "../../components/Eft";
import Utilities from "../../classes/Utilities";
import { IUpdateCreditCardResult, IBindQuoteResult } from "../../classes/ExternalQuote";
import _ from "lodash";
import QuoteActions from "../../state/appActions";
import moment from "moment";


interface IAppStateUpdate {
    quoteId: number,
    methodPaymentCd: string,
    paymentInfo: IPaymentBag;
}

interface IState extends IQuoteStepState {
    downPaymentInstrument: IPaymentInfo,
    installmentInstrument: IPaymentInfo | null,
    billingAddress: IAddress | null,
    updateResult: IUpdateCreditCardResult | null,
    bindQuoteResult: IBindQuoteResult | null,
    invalidBillingAddress: boolean,
    useHomeAddressForBilling: boolean,
    saveCCInstallmentInstrument: boolean;
}

interface IProps extends IQuoteStepComponentProps {
    quoteId: number,
    insured: IPerson,
    paymentChoice: IPaymentChoice | null,
    methodPaymentCd: string,
    updateAppStateViolations: (quoteId: number) => Promise<any>,
    bindQuote: (quoteId: number) => Promise<IBindQuoteResponse>,
    getDocuments: (quoteId: number) => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class Payment extends QuoteStepBase<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.next = this.next.bind(this);
        this.stopUpdate = this.stopUpdate.bind(this);
        this.onBillingAddressChanged = this.onBillingAddressChanged.bind(this);
        this.onDownpaymentInstrumentChanged = this.onDownpaymentInstrumentChanged.bind(this);
        this.onInstallmentInstrumentChanged = this.onInstallmentInstrumentChanged.bind(this);

        const _downPaymentInstrument: ICreditCardInfo = {
            cardHolderFirstName: "",
            cardHolderLastName: "",
            cardNumber: "",
            cardExpirationFull: "",
            cardExpirationMonth: 0,
            cardExpirationYear: 0,
            instrumentType: "CC",
            billingAddress: null
        };

        const _installmentInstrument = this.getDefaultInstallmentInstrument();

        this.state = {
            downPaymentInstrument: _downPaymentInstrument,
            installmentInstrument: _installmentInstrument,
            billingAddress: emptyAddress,
            updatingStore: false,
            saveCCInstallmentInstrument: false,
            useHomeAddressForBilling: false,
            invalidBillingAddress: true,
            updateResult: null,
            bindQuoteResult: null,
            errors: null
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.reportCurrentStep();

        this.setState({ ...this.state, updatingStore: false, errors: null });

        validationUtils.subscribeToValidationFailure(FULL_QUOTE_STEPS.PAYMENT,
            validationData => {
                this.setState({ ...this.state, updatingStore: false, errors: validationData });
            });
    }

    componentWillUnmount() {
        validationUtils.unsubscribeFromValidationFailure(FULL_QUOTE_STEPS.PAYMENT);
    }

    //static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
    //    if (nextProps.meta.propsSource === COMPONENTS.PROPS_SOURCES.APP_STORE) {
    //        let insured = nextProps.insured;
    //        if (insured && insured.address) {
    //            const { address } = { ...insured };

    //            return {
    //                ...prevState,
    //                billingAddress: address
    //            };
    //        }
    //    }

    //    return prevState;
    //}

    onBillingAddressChanged(result: IAddressChangeResult) {
        this.setState({
            ...this.state, billingAddress: result.address, invalidBillingAddress: !(result.hasOwnProperty("isValid") && result.isValid),
            downPaymentInstrument: { ...this.state.downPaymentInstrument, billingAddress: result.address },
            installmentInstrument: this.state.installmentInstrument != null
                ? { ...this.state.installmentInstrument, billingAddress: result.address }
                : null
        });
    }

    onDownpaymentInstrumentChanged(result: IPaymentInfoChangeResult) {
        let useCcForInstallment = this.state.saveCCInstallmentInstrument;

        if (useCcForInstallment) {
            this.setState({
                ...this.state, downPaymentInstrument: result.paymentInstrument, installmentInstrument: result.paymentInstrument
            });
        }
        else {
            this.setState({
                ...this.state, downPaymentInstrument: result.paymentInstrument
            });
        }
    }

    onInstallmentInstrumentChanged(result: IPaymentInfoChangeResult) {
        this.setState({
            ...this.state, installmentInstrument: result.paymentInstrument
        });
    }

    stopUpdate() {
        this.setState({ ...this.state, updatingStore: false });
    }

    changeUseHomeAddressAsBilling() {
        let useHomeAddress = !this.state.useHomeAddressForBilling;

        if (useHomeAddress) {
            let insured = this.props.insured;
            if (insured && insured.address) {
                const { address } = { ...insured };
                this.setState({ useHomeAddressForBilling: useHomeAddress, billingAddress: address },
                    () => {
                        if (address) {
                            this.onBillingAddressChanged({ address, isFull: true, isValid: true });
                        }
                    });
            }
        }
        else {
            this.setState({ ...this.state, useHomeAddressForBilling: useHomeAddress, billingAddress: emptyAddress });
        }
    }

    changeUseCCAsInstallmentInstrument() {
        let useCcForInstallment = !this.state.saveCCInstallmentInstrument;

        if (useCcForInstallment)
        {
            const _downPaymentInstrument = this.state.downPaymentInstrument as ICreditCardInfo;

            this.setState({ saveCCInstallmentInstrument: useCcForInstallment, installmentInstrument: _downPaymentInstrument },
                () => {
                    this.onInstallmentInstrumentChanged({ paymentInstrument: _downPaymentInstrument });
                });
        }
        else {
            this.setState({ ...this.state, saveCCInstallmentInstrument: useCcForInstallment, installmentInstrument: this.getDefaultInstallmentInstrument() });
        }
    }

    getDefaultInstallmentInstrument() {
        let _installmentInstrument: IPaymentInfo | null = { instrumentType: "", billingAddress: null };

        if (this.props.methodPaymentCd === "EFT CC") {
            (_installmentInstrument as ICreditCardInfo) = {
                cardHolderFirstName: "",
                cardHolderLastName: "",
                cardNumber: "",
                cardExpirationFull: "",
                cardExpirationMonth: 0,
                cardExpirationYear: 0,
                instrumentType: "CC",
                billingAddress: null
            };
        }
        else if (this.props.methodPaymentCd === "EFT") {
            (_installmentInstrument as IEftInfo) = {
                accountNumber: "",
                bankName: "",
                transitNumber: "",
                instrumentType: "EFT",
                billingAddress: null
            };
        }
        else {
            _installmentInstrument = null;
        }

        return _installmentInstrument;
    }

    next() {
        this.setState({ updatingStore: true, updateResult : null, bindQuoteResult: null }, () => {
            const paymentInfo = {
                downPaymentInstrument: this.state.downPaymentInstrument,
                installmentInstrument: this.state.installmentInstrument,
            };

            this.props.updateAppState({ quoteId: this.props.quoteId, methodPaymentCd: this.props.methodPaymentCd, paymentInfo: paymentInfo })
                .then((response) => {
                    this.setState({ updateResult: response.data }, () => {
                        if (null !== this.state.updateResult && this.state.updateResult.errors && 0 < this.state.updateResult.errors.length) {
                            this.stopUpdate();
                            return;
                        }
                        this.props.bindQuote(this.props.quoteId)
                            .then((bindResponse: IBindQuoteResponse) => {
                                this.setState({ bindQuoteResult: bindResponse.data.bindQuoteResult }, () => {
                                    if (null !== this.state.bindQuoteResult && this.state.bindQuoteResult.errors && 0 < this.state.bindQuoteResult.errors.length) {
                                        this.stopUpdate();
                                        return;
                                    }
                                    else {
                                        this.props.getDocuments(this.props.quoteId)
                                            .then(() => {
                                                this.props.history.push(this.props.PATHS.FQ_FINISH);
                                            },
                                                () => this.stopUpdate());
                                    }
                                });
                            },
                            () => this.stopUpdate());
                    });
                },
                () => this.stopUpdate()
            );
        });
    }

    showErrors(errors: Array<string> | undefined) {
        if (!errors || 0 === errors.length) return null;
        return <Alert variant={"danger"}>
            <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
            <p>Please review errors below</p>
            <ul>
                {
                    errors.map((error) => (
                        <li key={Utilities.uniqueId()}>
                            <p className="text-uppercase" dangerouslySetInnerHTML={{ __html: error }}></p>
                        </li>
                    ))
                }
            </ul>
        </Alert>;
    }

    render() {
        const total = (this.props.paymentChoice?.firstPay ?? 0) + +(3.50);
        return (
            <QuoteStep id="payment">
                <ValidatableForm hasErrors={null != this.state.errors}>
                    <div className="titleArea">
                        <h5>Payment Info</h5>
                    </div>

                    { this.showErrors(this.state.updateResult?.errors) }
                    { this.showErrors(this.state.bindQuoteResult?.errors) }

                    <p className="info">We will collect today's payment and get you on the road!</p>

                    <div className="policy-details">
                        <div className="payment-choice">
                            <div className="item">
                                <label className="left-cell">{Utilities.formatCurrency(this.props.paymentChoice?.firstPay)}</label>
                                <label className="right-cell">Due today</label>
                            </div>
                            <p style={{ color: "red" }}>*Disclaimer: A convenience fee in the amount of $3.50 will be added to the payment amount above and charged by our CC payment processor TranzPay. If you DO NOT agree to the $3.50 convenience fee, do not proceed with this payment!</p>
                            {
                                this.props.paymentChoice?.autopay
                                    ? <p style={{ color: "red" }}>Customers enrolled in AutoPay (EFT) are responsible for maintaining sufficient funds. A $5.00 decline fee will apply to any AutoPay payment that is returned or declined.</p>
                                    : null
                            }
                            <p style={{ color: "red" }}><b>Total including fee: {Utilities.formatCurrency(total)} </b></p>
                        </div>
                    </div>

                    <div className="break-2" />

                    <CreditCard
                        creditCard={this.state.downPaymentInstrument as ICreditCardInfo}
                        errors={validationUtils.extractErrorsByPrefix(this.state.errors, "downPaymentInstrument.")}
                        onChange={this.onDownpaymentInstrumentChanged}
                    />

                    {
                        this.props.methodPaymentCd === "EFT" || this.props.methodPaymentCd === "EFT CC"
                            ?
                                this.props.methodPaymentCd === "EFT CC"
                                ?
                                <div>
                                    <div className="break-2" />
                                    <div className="titleArea">
                                        <h5>AUTOPAY INFORMATION</h5>
                                    </div>
                                    <ComplexCheckbox
                                        children={
                                            <p>Use down payment credit card data</p>
                                        }
                                        value={this.state.saveCCInstallmentInstrument}
                                        onChange={(e) => this.changeUseCCAsInstallmentInstrument()} />

                                    <CreditCard
                                        creditCard={this.state.installmentInstrument as ICreditCardInfo}
                                        errors={validationUtils.extractErrorsByPrefix(this.state.errors, "installmentInstrument.")}
                                        onChange={this.onInstallmentInstrumentChanged}
                                        disabled={this.state.saveCCInstallmentInstrument} />
                                </div>
                                :
                                <div>
                                    <div className="break-2" />
                                    <div className="titleArea">
                                        <h5>EFT Installment Payment Info</h5>
                                        {
                                            this.props.paymentChoice
                                                ?
                                                <Form.Label>
                                                    {`Your 1st Installment will be withdrawn from the below account on ${moment(new Date(this.props.paymentChoice.installmentDueDt)).format("MM/DD/YYYY")} in the amount of ${Utilities.formatCurrency(this.props.paymentChoice.regularPay)}`}
                                                </Form.Label>
                                                :
                                                null
                                        }
                                    </div>
                                    <Eft
                                        eft={this.state.installmentInstrument as IEftInfo}
                                        errors={validationUtils.extractErrorsByPrefix(this.state.errors, "installmentInstrument.")}
                                        onChange={this.onInstallmentInstrumentChanged} />
                                </div>
                            : null
                    }

                    <div className="titleArea">
                        <h5>Billing Address</h5>
                    </div>

                    <ComplexCheckbox
                        children={
                            <p>Check here if you would like to use the same address info for billing as your maling address.</p>
                        }
                        value={this.state.useHomeAddressForBilling}
                        onChange={(e) => this.changeUseHomeAddressAsBilling()} />

                    <Address
                        errors={validationUtils.extractErrorsByPrefix(this.state.errors, "billingAddress.")}
                        value={this.state.billingAddress ? this.state.billingAddress : null}
                        noAddressVerification={true}
                        onChange={this.onBillingAddressChanged} />

                    <div className="btnContainer">
                        <ButtonContinue
                            label="Purchase Policy"
                            disabled={this.state.updatingStore || this.state.invalidBillingAddress}
                            showLoading={this.state.updatingStore}
                            onClick={this.next} />
                    </div>
                </ValidatableForm>
            </QuoteStep>

        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        quoteId: state.externalQuote?.rtrQuoteId,
        insured: state.externalQuote?.insured,
        paymentChoice: state.paymentChoice,
        methodPaymentCd: state.paymentChoice?.methodPaymentCd,
        meta: { propsSource: COMPONENTS.PROPS_SOURCES.APP_STORE },
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(FULL_QUOTE_STEPS.PAYMENT)),
        updateAppState: (update: IAppStateUpdate) => dispatch(fullQuoteActions.savePaymentInfo(update.quoteId, update.methodPaymentCd, update.paymentInfo)),
        bindQuote: (quoteId: number) => dispatch(fullQuoteActions.bindQuote(quoteId)),
        getDocuments: (rtrQuoteId: number) => dispatch(fullQuoteActions.getDocuments(rtrQuoteId)),
    };
};

export default connect(mapProps, mapDispatch)(Payment);